// Painel de Monitoramento, "Panorama" (instructor feedback pt 3): structural counts of the plan and a drill-down
// eixo → objetivo → KR → iniciativas and back. Built from the structure payload; each level is a URL entry so
// browser Back and reload work. Progress (BLD-17) appears once some initiative below a node is validated.
import { ChevronRight } from "lucide-react";
import { cx } from "./ui.tsx";
import { ProgressBar } from "./progress.tsx";
import { StatusPill } from "./status.tsx";
import { GuardedLink } from "./plan-shell.tsx";
import { useUrlState } from "../lib/nav.tsx";
import type { InitiativeSummary, StructureAxis, StructureKR, StructureObjective } from "../lib/types.ts";
import { axisTones } from "../../shared/colors.ts";
import { averageProgress, effectiveProgress } from "../../shared/progress.ts";
import { axisLabel, initiativeLabel, krLabel, objectiveLabel } from "../../shared/numbering.ts";

const liveInits = (k: StructureKR) => k.initiatives.filter((i) => !i.archived_at);
const krsOf = (o: StructureObjective) => o.key_results.filter((k) => !k.archived_at);
const objsOf = (a: StructureAxis) => a.objectives.filter((o) => !o.archived_at);

const hasValidated = (inits: InitiativeSummary[]) => inits.some((i) => i.status === "validated");
const krProgress = (k: StructureKR) => (hasValidated(liveInits(k)) ? averageProgress(liveInits(k).map(effectiveProgress)) : null);
const objProgress = (o: StructureObjective) =>
  krsOf(o).some((k) => krProgress(k) !== null) ? averageProgress(krsOf(o).map((k) => krProgress(k) ?? 0)) : null;
const axisProgress = (a: StructureAxis) =>
  objsOf(a).some((o) => objProgress(o) !== null) ? averageProgress(objsOf(a).map((o) => objProgress(o) ?? 0)) : null;

interface Counts { objectives?: number; krs?: number; initiatives: number; actions: number }
const countKr = (k: StructureKR): Counts => ({ initiatives: liveInits(k).length, actions: liveInits(k).reduce((s, i) => s + i.action_count, 0) });
const sum = (xs: Counts[], extra: Partial<Counts>): Counts => ({
  initiatives: xs.reduce((s, c) => s + c.initiatives, 0),
  actions: xs.reduce((s, c) => s + c.actions, 0),
  krs: xs.reduce((s, c) => s + (c.krs ?? 0), 0),
  ...extra,
});
const countObj = (o: StructureObjective): Counts => sum(krsOf(o).map(countKr), { krs: krsOf(o).length });
const countAxis = (a: StructureAxis): Counts => sum(objsOf(a).map(countObj), { objectives: objsOf(a).length });

function countLine(c: Counts) {
  return [
    c.objectives !== undefined && `${c.objectives} objetivo(s)`,
    c.krs !== undefined && `${c.krs} KR(s)`,
    `${c.initiatives} iniciativa(s)`,
    `${c.actions} ação(ões)`,
  ].filter(Boolean).join(" · ");
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-panel border border-line bg-surface p-4 shadow-e1">
      <span className="eyebrow block">{label}</span>
      <span className="num mt-1 block text-[30px] leading-tight font-medium">{value}</span>
    </div>
  );
}

/** Horizontal bars, one per child, in the axis color (single series: the title names it, no legend). */
function ActionsChart({ title, rows }: { title: string; rows: { id: string; label: string; value: number; color: string }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return null;
  return (
    <figure className="rounded-panel border border-line bg-surface p-4 shadow-e1">
      <figcaption className="mb-3 font-bold">{title}</figcaption>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_2.5rem] items-center gap-3 text-sm" title={`${r.label}: ${r.value} ação(ões)`}>
            <span className="truncate text-muted">{r.label}</span>
            <span className="h-4 rounded-r-[4px] bg-paper">
              <span className="block h-4 rounded-r-[4px]" style={{ width: `${(r.value / max) * 100}%`, minWidth: r.value ? 4 : 0, background: r.color }} />
            </span>
            <span className="num text-right text-[13px] font-medium">{r.value}</span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

function ChildCard({ color, over, title, line, progress, onOpen }: { color: string; over: string; title: string; line: string; progress: number | null; onOpen: () => void }) {
  const t = axisTones(color);
  return (
    <button type="button" onClick={onOpen}
      className="lift group flex w-full items-start gap-2 rounded-panel border border-line bg-surface p-4 text-left"
      style={{ borderLeft: `4px solid ${t.base}` }}>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-bold tracking-wide uppercase" style={{ color: t.text }}>{over}</span>
        <span className="block font-semibold" style={{ color: t.text }}>{title}</span>
        <span className="mt-1 block text-xs text-muted">{line}</span>
        {progress !== null && <ProgressBar className="mt-2" value={progress} color={t.base} label={`Execução: ${title}`} />}
      </span>
      <ChevronRight className="mt-1 size-4 shrink-0 text-muted group-hover:text-primary" aria-hidden />
    </button>
  );
}

export function Panorama({ planId, axes: all }: { planId: string; axes: StructureAxis[] }) {
  const [u, setU] = useUrlState({ pe: "", po: "", pk: "" });
  const axes = all.filter((a) => !a.archived_at);
  const axis = axes.find((a) => a.id === u.pe);
  const objective = axis && objsOf(axis).find((o) => o.id === u.po);
  const kr = objective && krsOf(objective).find((k) => k.id === u.pk);
  const go = (patch: { pe?: string; po?: string; pk?: string }) => setU({ pe: "", po: "", pk: "", ...patch }, { push: true });

  const crumbs: { label: string; onClick: () => void }[] = [{ label: "Plano", onClick: () => go({}) }];
  if (axis) crumbs.push({ label: `${axisLabel(axis.number)}: ${axis.name}`, onClick: () => go({ pe: axis.id }) });
  if (axis && objective) crumbs.push({ label: objectiveLabel(objective.number), onClick: () => go({ pe: axis.id, po: objective.id }) });
  if (axis && objective && kr) crumbs.push({ label: krLabel(kr.number), onClick: () => go({ pe: axis.id, po: objective.id, pk: kr.id }) });

  let tiles: [string, number | string][];
  let body: React.ReactNode;
  let chart: { title: string; rows: { id: string; label: string; value: number; color: string }[] };

  if (axis && objective && kr) {
    const c = countKr(kr);
    const p = krProgress(kr);
    tiles = [["Iniciativas", c.initiatives], ["Ações", c.actions], ...(p !== null ? [["Execução do KR", `${p}%`] as [string, string]] : [])];
    body = (
      <ul className="grid gap-3 md:grid-cols-2">
        {liveInits(kr).map((i) => (
          <li key={i.id}>
            <GuardedLink href={`/planos/${planId}/iniciativas/${i.id}`}
              className="lift block rounded-panel border border-line bg-surface p-4">
              <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-muted uppercase">
                <span className="size-2 rounded-full" style={{ background: axis.color }} aria-hidden />{initiativeLabel(i.number)}
              </span>
              <span className="flex flex-wrap items-center gap-2"><span className="font-semibold text-ink">{i.title}</span><StatusPill status={i.status} /></span>
              <span className="mt-1 block text-xs text-muted">{i.unit_name ?? "Unidade não indicada"} · {i.action_count} ação(ões)</span>
              {i.status === "validated" && <ProgressBar className="mt-2" value={i.progress} color={axis.color} label={`Execução: ${i.title}`} />}
            </GuardedLink>
          </li>
        ))}
        {liveInits(kr).length === 0 && <li className="text-sm text-muted">Ainda não há iniciativas neste KR.</li>}
      </ul>
    );
    chart = { title: "Ações por iniciativa", rows: liveInits(kr).map((i) => ({ id: i.id, label: `${initiativeLabel(i.number)}: ${i.title}`, value: i.action_count, color: axis.color })) };
  } else if (axis && objective) {
    const c = countObj(objective);
    const p = objProgress(objective);
    tiles = [["KRs", c.krs ?? 0], ["Iniciativas", c.initiatives], ["Ações", c.actions], ...(p !== null ? [["Execução do objetivo", `${p}%`] as [string, string]] : [])];
    body = (
      <div className="grid gap-3 md:grid-cols-2">
        {krsOf(objective).map((k) => (
          <ChildCard key={k.id} color={axis.color} over={krLabel(k.number)} title={k.title} line={countLine(countKr(k))} progress={krProgress(k)}
            onOpen={() => go({ pe: axis.id, po: objective.id, pk: k.id })} />
        ))}
        {krsOf(objective).length === 0 && <p className="text-sm text-muted">Ainda não há KRs neste objetivo.</p>}
      </div>
    );
    chart = { title: "Ações por KR", rows: krsOf(objective).map((k) => ({ id: k.id, label: `${krLabel(k.number)}: ${k.title}`, value: countKr(k).actions, color: axis.color })) };
  } else if (axis) {
    const c = countAxis(axis);
    const p = axisProgress(axis);
    tiles = [["Objetivos", c.objectives ?? 0], ["KRs", c.krs ?? 0], ["Iniciativas", c.initiatives], ["Ações", c.actions], ...(p !== null ? [["Execução do eixo", `${p}%`] as [string, string]] : [])];
    body = (
      <div className="grid gap-3 md:grid-cols-2">
        {objsOf(axis).map((o) => (
          <ChildCard key={o.id} color={axis.color} over={objectiveLabel(o.number)} title={o.title} line={countLine(countObj(o))} progress={objProgress(o)}
            onOpen={() => go({ pe: axis.id, po: o.id })} />
        ))}
        {objsOf(axis).length === 0 && <p className="text-sm text-muted">Ainda não há objetivos neste eixo.</p>}
      </div>
    );
    chart = { title: "Ações por objetivo", rows: objsOf(axis).map((o) => ({ id: o.id, label: `${objectiveLabel(o.number)}: ${o.title}`, value: countObj(o).actions, color: axis.color })) };
  } else {
    const per = axes.map(countAxis);
    const total = sum(per, { objectives: per.reduce((s, c) => s + (c.objectives ?? 0), 0) });
    tiles = [["Eixos", axes.length], ["Objetivos", total.objectives ?? 0], ["KRs", total.krs ?? 0], ["Iniciativas", total.initiatives], ["Ações", total.actions]];
    body = (
      <div className="grid gap-3 md:grid-cols-2">
        {axes.map((a, n) => (
          <ChildCard key={a.id} color={a.color} over={axisLabel(a.number)} title={a.name} line={countLine(per[n]!)} progress={axisProgress(a)}
            onOpen={() => go({ pe: a.id })} />
        ))}
      </div>
    );
    chart = { title: "Ações por eixo", rows: axes.map((a, n) => ({ id: a.id, label: `${axisLabel(a.number)}: ${a.name}`, value: per[n]!.actions, color: a.color })) };
  }

  return (
    <section aria-labelledby="panorama-title" className="mb-8">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="panorama-title" className="text-lg font-bold">Panorama do plano</h2>
        <nav aria-label="Nível do panorama" className="flex flex-wrap items-center gap-1 text-sm">
          {crumbs.map((c, n) => (
            <span key={n} className="flex items-center gap-1">
              {n > 0 && <ChevronRight className="size-3.5 text-muted" aria-hidden />}
              {n === crumbs.length - 1
                ? <span aria-current="location" className="font-semibold">{c.label}</span>
                : <button type="button" onClick={c.onClick} className="tap rounded-ctl px-1 font-semibold text-primary hover:underline">{c.label}</button>}
            </span>
          ))}
        </nav>
      </div>
      <div className={cx("grid grid-cols-2 gap-3", tiles.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4")}>
        {tiles.map(([label, value]) => <Tile key={label} label={label} value={value} />)}
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div>{body}</div>
        <ActionsChart title={chart.title} rows={chart.rows} />
      </div>
    </section>
  );
}
